import { isAxiosError } from 'axios'
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { useUserQuery } from 'api/user/queries'
import { useCountrySetup } from 'hooks/useCountrySetup'
import { useLoginProvider } from 'hooks/useLoginProvider'
import { useOriginalUrl } from 'hooks/useOriginalUrl'
import { useSecureStorageTokenProvider } from 'hooks/useSecureStorageTokenProvider'
import { ErrorPage } from 'pages/ErrorPage'
import { NoAccessPage } from 'pages/NoAccessPage'

import { setUnauthorizedUserHandler } from 'services/axios'

import { isUnauthorizedUserErrorType, UnauthorizedUserErrorType } from 'types/UnauthorizedErrorType'

import { PageLoader } from '../PageLoader'

export const NoAccessGuard = ({ children }: PropsWithChildren) => {
  const { pathname, search } = useLocation()

  const { setOriginalUrl } = useOriginalUrl()

  const redirectToSecureStorageTokenProvider = useSecureStorageTokenProvider()
  const { redirectToLogin } = useLoginProvider()

  const [isRedirecting, setIsRedirecting] = useState(false)

  const { data, error, isPending } = useUserQuery()

  const isAuthenticated = Boolean(data?.user?.uuid)

  useCountrySetup({ country: data?.user.country })

  const fullUrl = `${pathname}${search}`

  const fullUrlRef = useRef(fullUrl)

  useEffect(() => {
    fullUrlRef.current = fullUrl
  }, [fullUrl])

  const unauthorizedUserHandler = useCallback((type: UnauthorizedUserErrorType) => {
    setOriginalUrl(fullUrlRef.current)
    setIsRedirecting(true)
    if (type === 'secure_storage_token_expired') redirectToSecureStorageTokenProvider()
    else if (type === 'unauthorized') redirectToLogin()
  }, [redirectToLogin, redirectToSecureStorageTokenProvider, setOriginalUrl])

  useEffect(() => {
    setUnauthorizedUserHandler(type => unauthorizedUserHandler(type))
    return () => setUnauthorizedUserHandler(null)
  }, [unauthorizedUserHandler])

  const showNoAccessPage = isAxiosError(error) && error.response?.status === 403

  const showPageLoader = isPending || isUnauthorizedUserErrorType(error) || isRedirecting

  if (showNoAccessPage) return <NoAccessPage />

  if (showPageLoader) return <PageLoader />

  if (error) return <ErrorPage />

  if (isAuthenticated) return <>{children}</>

  return <PageLoader />
}
